// src/services/geocodeClient.ts
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';

export type ReverseGeocodeDetails = {
  ok?: boolean; label?: string; error?: string; provider?: string; usedFallback?: boolean;
  waterName?: string | null; waterKind?: string | null; waterType?: 'freshwater' | 'saltwater' | 'brackish' | 'unknown' | string;
  featureCategory?: string | null; featureType?: string | null; city?: string | null; state?: string | null; country?: string | null; countryCode?: string | null;
};
type ReverseGeocodeResult = ReverseGeocodeDetails;
type ForwardGeocodeRow = { lat: number; lon: number; label: string };
type ForwardGeocodeResult = { ok?: boolean; results?: ForwardGeocodeRow[]; error?: string };

const REVERSE_CACHE_PREFIX = 'roddo.geocode.reverse.v1';
const FORWARD_CACHE_PREFIX = 'roddo.geocode.forward.v1';
const REVERSE_TTL_MS = 1000 * 60 * 60 * 24 * 7;
const FORWARD_TTL_MS = 1000 * 60 * 60 * 24;
const REVERSE_MEMORY = new Map<string, { ts: number; value: ReverseGeocodeDetails | null }>();
const FORWARD_MEMORY = new Map<string, { ts: number; value: ForwardGeocodeRow[] }>();
const reverseInflight = new Map<string, Promise<ReverseGeocodeDetails | null>>();
const forwardInflight = new Map<string, Promise<ForwardGeocodeRow[]>>();

function toNum(v: any): number | null {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
  return Number.isFinite(n) ? n : null;
}

function now() {
  return Date.now();
}

function safeStorageGet<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function reverseKey(lat: number, lon: number) {
  return `${REVERSE_CACHE_PREFIX}:${Math.round(lat * 1000) / 1000}:${Math.round(lon * 1000) / 1000}`;
}

function forwardKey(q: string, limit: number) {
  return `${FORWARD_CACHE_PREFIX}:${String(q).trim().toLowerCase()}:${limit}`;
}

function readReverseCache(key: string): ReverseGeocodeDetails | null | undefined {
  const mem = REVERSE_MEMORY.get(key);
  if (mem && now() - mem.ts < REVERSE_TTL_MS) return mem.value;

  const stored = safeStorageGet<{ ts?: number; value?: ReverseGeocodeDetails | string | null }>(key);
  if (stored && typeof stored.ts === 'number' && now() - stored.ts < REVERSE_TTL_MS) {
    const value = typeof stored.value === 'string' ? { ok: true, label: stored.value } : (stored.value && typeof stored.value === 'object' ? stored.value as ReverseGeocodeDetails : null);
    REVERSE_MEMORY.set(key, { ts: stored.ts, value });
    return value;
  }
  return undefined;
}

function writeReverseCache(key: string, value: ReverseGeocodeDetails | null) {
  const payload = { ts: now(), value };
  REVERSE_MEMORY.set(key, payload);
  safeStorageSet(key, payload);
}

function readForwardCache(key: string): ForwardGeocodeRow[] | undefined {
  const mem = FORWARD_MEMORY.get(key);
  if (mem && now() - mem.ts < FORWARD_TTL_MS) return mem.value;

  const stored = safeStorageGet<{ ts?: number; value?: ForwardGeocodeRow[] }>(key);
  if (stored && typeof stored.ts === 'number' && now() - stored.ts < FORWARD_TTL_MS && Array.isArray(stored.value)) {
    const value = stored.value.filter((row) => Number.isFinite(toNum((row as any)?.lat)) && Number.isFinite(toNum((row as any)?.lon)));
    FORWARD_MEMORY.set(key, { ts: stored.ts, value });
    return value;
  }
  return undefined;
}

function writeForwardCache(key: string, value: ForwardGeocodeRow[]) {
  const payload = { ts: now(), value };
  FORWARD_MEMORY.set(key, payload);
  safeStorageSet(key, payload);
}

/**
 * Hard timeout wrapper so geocoding can't stall the app.
 */
async function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let t: any;
  const timeout = new Promise<T>((_, reject) => {
    t = setTimeout(() => reject(new Error(`${label} timeout after ${ms}ms`)), ms);
  });
  try {
    return await Promise.race([p, timeout]);
  } finally {
    clearTimeout(t);
  }
}

/**
 * Always get the Functions instance from the active Firebase app and pin region.
 * This avoids subtle misconfig where an imported `functions` is bound to the wrong project/region.
 */
function getFns(): Functions {
  const app = getApp();
  return getFunctions(app, 'us-central1');
}

async function ensureCallableAuth(): Promise<boolean> {
  try {
    const app = getApp();
    const auth = getAuth(app);
    const user = auth.currentUser;
    if (!user?.uid) return false;

    // Ensure the SDK has a current token before an authenticated callable.
    await user.getIdToken(false);
    return true;
  } catch (error) {
    console.warn('[geocode] callable auth not ready', error);
    return false;
  }
}

export async function reverseGeocodeDetailsServer(
  lat: number,
  lon: number,
): Promise<ReverseGeocodeDetails | null> {
  const latNum = toNum(lat);
  const lonNum = toNum(lon);

  if (latNum === null || lonNum === null) return null;

  const key = reverseKey(latNum, lonNum);
  const cached = readReverseCache(key);
  if (cached !== undefined) return cached;

  if (reverseInflight.has(key)) return reverseInflight.get(key)!;

  const task = (async () => {
    try {
      if (!(await ensureCallableAuth())) {
        if (import.meta.env.DEV) {
          console.log('[geocode] reverse skipped until Firebase JS auth is ready');
        }
        return null;
      }

      const fn = httpsCallable<{ lat: number; lon?: number; lng?: number }, ReverseGeocodeResult>(
        getFns(),
        'reverseGeocodeCallable',
      );

      const call = fn({ lat: latNum, lon: lonNum, lng: lonNum });
      const res = await withTimeout(call as any, 7000, 'reverseGeocodeCallable');
      const data = (res as any)?.data as ReverseGeocodeResult | undefined;

      if (data?.ok && typeof data.label === 'string' && data.label.trim()) {
        const value = { ...data, label: data.label.trim() };
        writeReverseCache(key, value);
        return value;
      }

      if (data?.error) console.warn('[geocode] reverseGeocodeCallable error:', data.error);
      else console.warn('[geocode] reverseGeocodeCallable unexpected response:', data);

      writeReverseCache(key, null);
      return null;
    } catch (err) {
      console.warn('[geocode] reverseGeocodeCallable failed; falling back', err);
      return null;
    } finally {
      reverseInflight.delete(key);
    }
  })();

  reverseInflight.set(key, task);
  return task;
}


export async function reverseGeocodeServer(lat: number, lon: number): Promise<string | null> {
  const details = await reverseGeocodeDetailsServer(lat, lon);
  return typeof details?.label === 'string' && details.label.trim() ? details.label.trim() : null;
}

export async function forwardGeocodeServer(
  q: string,
  limit = 5,
): Promise<ForwardGeocodeRow[]> {
  const query = String(q ?? '').trim();
  if (!query) return [];

  const safeLimit = Math.max(1, Math.min(10, Number.isFinite(limit) ? limit : 5));
  const key = forwardKey(query, safeLimit);
  const cached = readForwardCache(key);
  if (cached) return cached;

  if (forwardInflight.has(key)) return forwardInflight.get(key)!;

  const task = (async () => {
    try {
      if (!(await ensureCallableAuth())) {
        if (import.meta.env.DEV) {
          console.log('[geocode] forward skipped until Firebase JS auth is ready');
        }
        return [];
      }

      const fn = httpsCallable<{ q: string; limit?: number }, ForwardGeocodeResult>(
        getFns(),
        'forwardGeocodeCallable',
      );

      const call = fn({ q: query, limit: safeLimit });
      const res = await withTimeout(call as any, 9000, 'forwardGeocodeCallable');

      const data = (res as any)?.data as ForwardGeocodeResult | undefined;
      const rowsRaw = Array.isArray(data?.results) ? data!.results : [];

      const rows = rowsRaw
        .map((r: any) => ({
          lat: toNum(r?.lat) ?? NaN,
          lon: toNum(r?.lon ?? r?.lng) ?? NaN,
          label: String(r?.label ?? '').trim() || 'Result',
        }))
        .filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lon));

      writeForwardCache(key, rows);
      return rows;
    } catch (e) {
      console.warn('[geocode] forwardGeocodeCallable failed; returning empty', e);
      return [];
    } finally {
      forwardInflight.delete(key);
    }
  })();

  forwardInflight.set(key, task);
  return task;
}
